import { FC, useCallback, useMemo, useState } from 'react';
import { FieldHelperProps, FieldInputProps, FieldMetaProps } from 'formik';
import useDebouncedValue from 'hooks/useDebouncedValue';
import useIcs2CodelistOptions from 'components/ui/composed/declarations/formSelect/useIcs2CodelistOptions';
import { ICS2CodelistsMap } from 'store/codelists/code';
import { DeclarationTypes } from 'store/declarations/enums/common/declaration-types';
import FormSelect from '../formSelect/FormSelect';

const SEARCH_DEBOUNCE_MS = 500;

export interface UnLocodeSelectProps {
    refNumber?: string;
    label?: string;
    fieldProps: FieldInputProps<any>;
    fieldMeta?: FieldMetaProps<any>;
    fieldHelper?: FieldHelperProps<any>;
    required?: boolean;
    placeholder?: string;
    disabled?: boolean;
    style?: React.CSSProperties;
    countryCode?: string;
}

// CL244 (UN/LOCODE extended) holds ~100k entries, so it is never loaded upfront: the options are
// fetched server-side as the user types, and a pre-filled value is resolved separately via seedQuery.
const UnLocodeSelect: FC<UnLocodeSelectProps> = ({
    refNumber,
    label,
    fieldProps,
    fieldMeta,
    fieldHelper,
    required,
    placeholder,
    disabled = false,
    style,
    countryCode,
}) => {
    // What the user has typed. Kept separate from the query sent to the server, because it is also
    // the controlled `searchValue` of the select and must stay empty while the selection is shown.
    const [searchQuery, setSearchQuery] = useState<string | undefined>(undefined);
    const [dropdownOpened, setDropdownOpened] = useState(false);
    const debouncedSearchQuery = useDebouncedValue(searchQuery, SEARCH_DEBOUNCE_MS);

    const seedQuery = useMemo(
        () => (typeof fieldProps.value === 'string' && fieldProps.value ? fieldProps.value : undefined),
        [fieldProps.value]
    );

    // undefined means nothing is fetched yet — the dropdown has never been opened. Once it has:
    // what the user typed, or, before they type anything, the pre-filled value, so an edit form
    // lists the current selection's neighbourhood instead of the top of the codelist.
    const fetchQuery = useMemo(() => {
        if (!dropdownOpened) return undefined;
        return debouncedSearchQuery || seedQuery || '';
    }, [dropdownOpened, debouncedSearchQuery, seedQuery]);

    const { codelistOptions, loading } = useIcs2CodelistOptions({
        codelistName: ICS2CodelistsMap.CL244,
        declarationType: DeclarationTypes.ICS2,
        query: fetchQuery,
        // CL244 is query-driven; the pre-filled value is resolved through seedQuery instead.
        fetchOnMount: false,
        seedQuery,
        countryCode,
    });

    const handleDropdownVisibleChange = useCallback((open: boolean) => {
        if (open) setDropdownOpened(true);
    }, []);

    const clearSearch = useCallback(() => setSearchQuery(undefined), []);

    return (
        <FormSelect
            refNumber={refNumber}
            label={label}
            required={required}
            placeholder={placeholder ?? ''}
            disabled={disabled}
            style={style}
            selectOptions={codelistOptions}
            loading={loading}
            filterOption={false}
            onSearch={setSearchQuery}
            searchValue={searchQuery ?? ''}
            onDropdownVisibleChange={handleDropdownVisibleChange}
            onChange={clearSearch}
            onClear={clearSearch}
            fieldProps={fieldProps}
            fieldMeta={fieldMeta}
            fieldHelper={fieldHelper}
        />
    );
};

export default UnLocodeSelect;
